/**
 * Owns the active type scale (see typeScale.ts) the way LocaleProvider owns
 * the locale: read once from storage, applied to <html> in a layout effect
 * (before paint), and handed to App.tsx so Ant Design's size tokens follow
 * the switch.
 */
import { createContext, useCallback, useContext, useLayoutEffect, useState, type ReactNode } from 'react';
import { applyTypeScale, readTypeScale, type TypeScale } from './typeScale';

type TypeScaleContextValue = {
  typeScale: TypeScale;
  setTypeScale: (next: TypeScale) => void;
};

const TypeScaleContext = createContext<TypeScaleContextValue | undefined>(undefined);

export function TypeScaleProvider({ children }: { children: ReactNode }) {
  const [typeScale, setState] = useState<TypeScale>(() => readTypeScale());

  // The attribute every rung reads — set before the browser paints (a layout
  // effect), on mount and on every change, so a remembered Comfortable never
  // shows a Standard frame first.
  useLayoutEffect(() => {
    applyTypeScale(typeScale);
  }, [typeScale]);

  const setTypeScale = useCallback((next: TypeScale) => setState(next), []);

  return (
    <TypeScaleContext.Provider value={{ typeScale, setTypeScale }}>
      {children}
    </TypeScaleContext.Provider>
  );
}

export function useTypeScale(): TypeScaleContextValue {
  const ctx = useContext(TypeScaleContext);
  if (!ctx) throw new Error('useTypeScale must be used within TypeScaleProvider');
  return ctx;
}
